import { t } from "i18next";
import { IWebsiteBuilderNodeBase, TWebsiteBuilderNode } from "../websiteBuilderNode";
import { TWebsiteBuilderElement } from "../element/websiteBuilderElement";
import { IWebsiteBuilderContent } from "./websiteBuilderContent";
import { IWebsiteBuilderHeader } from "./websiteBuilderHeader";
import { IWebsiteBuilderFooter } from "./websiteBuilderFooter";
import { TWebsiteBuilderContainer } from "../container/websiteBuilderContainer";
import { min, max } from "lodash";

export type TWebsiteBuilderSectionChild = TWebsiteBuilderContainer | TWebsiteBuilderElement;
export type TWebsiteBuilderSectionChildType = TWebsiteBuilderSectionChild["type"];

export interface IWebsiteBuilderSectionDataBase {
  type: string;
  height: number;
}

export interface IWebsiteBuilderSectionBase extends IWebsiteBuilderNodeBase {
  type: "section";
  section: IWebsiteBuilderSectionDataBase;
  children: TWebsiteBuilderSectionChild[];
}

export type TWebsiteBuilderSection = IWebsiteBuilderHeader | IWebsiteBuilderContent | IWebsiteBuilderFooter;

export type TWebsiteBuilderSectionData = TWebsiteBuilderSection["section"];
export type TWebsiteBuilderSectionType = TWebsiteBuilderSectionData["type"];

export type TWebsiteBuilderMainSection = IWebsiteBuilderContent;

export function isWebsiteBuilderMainSection(node: TWebsiteBuilderNode): node is TWebsiteBuilderMainSection {
  return node?.type === "section" && node.section.type === "content";
}

export const sectionTitles: Record<TWebsiteBuilderSectionType, string> = {
  header: t("Header"),
  content: t("Section"),
  footer: t("Footer"),
};

export const WEBSITE_BUILDER_SECTION_CHILD_PLACEMENT_BOUNDARY = 3;

export const WEBSITE_BUILDER_SECTION_CLASS_NAME = "website-builder-section";

function getSectionMinZIndex(node: TWebsiteBuilderSection): number | null {
  const zIndices = node?.children?.map(node =>
    node.type === "element" ? node.element.zIndex : node.container.zIndex,
  );
  const minZIndex = min(zIndices);

  return minZIndex;
}

function getSectionMaxZIndex(node: TWebsiteBuilderSection): number | null {
  const zIndices = node?.children?.map(node =>
    node.type === "element" ? node.element.zIndex : node.container.zIndex,
  );
  const maxZIndex = max(zIndices);

  return maxZIndex;
}

export function getSectionNextZIndex(node: TWebsiteBuilderSection): number {
  const maxZIndex = getSectionMaxZIndex(node);
  const nextZIndex = maxZIndex == null ? 0 : maxZIndex + 1;

  return nextZIndex;
}

export function canBringToBackElementInSection(node: TWebsiteBuilderElement, parentNode: TWebsiteBuilderSection) {
  const minZIndex = getSectionMinZIndex(parentNode);

  return minZIndex != null && parentNode.children.length > 1 && node.element.zIndex !== minZIndex;
}

export function canBringToFrontElementInSection(node: TWebsiteBuilderElement, parentNode: TWebsiteBuilderSection) {
  const maxZIndex = getSectionMaxZIndex(parentNode);

  return maxZIndex != null && parentNode.children.length > 1 && node.element.zIndex !== maxZIndex;
}
